import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import Select from "react-select";
import secureLocalStorage from "react-secure-storage";

const EducationInfo = () => {
  const [form, setForm] = useState({});
  const [educationList, setEducationList] = useState([]);
  const [editingIndex, setEditingIndex] = useState(null);
  const [gradingType, setGradingType] = useState("");
  const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);
	const educationPost = async () => {
		setLoading(true);
		setError(null);

		try {
			const payload = {
				userId: import.meta.env.VITE_API_USER,
				password: import.meta.env.VITE_API_PASS,
				user: secureLocalStorage.getItem("user"),
				token:secureLocalStorage.getItem("token"),
		    };
			const api = axios.create({
				baseURL: import.meta.env.VITE_API_BASE_URL,
				headers: { "Content-Type": "application/json" },
			});
			const response = await api.post("/education-list", payload);
			
			// Loop data (array)
			setEducationList(response.data || []);			
		} catch (err) {
			console.log(err);
		    setError(err.message || "Something went wrong");		   
		} finally {
		  setLoading(false);
		}
	};
	useEffect(() => {		
		educationPost();		
	}, []);
  

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSave = async () => {
	  try {
		// Create a FormData payload
		const payload = new FormData();

		// Add secure credentials
		payload.append("userId", import.meta.env.VITE_API_USER);
		payload.append("password", import.meta.env.VITE_API_PASS);
		payload.append("user", secureLocalStorage.getItem("user"));
		payload.append("token", secureLocalStorage.getItem("token"));
		// Append education form fields
		Object.entries(form).forEach(([key, value]) => {
		  if (value !== undefined && value !== null) {
			payload.append(key, value);
		  }
		});

		let res;

		// --- Update existing education record ---
		if (editingIndex !== null) {
			const api = axios.create({
				baseURL: import.meta.env.VITE_API_BASE_URL,
				headers: { "Content-Type": "application/json" },
			});
			const res = await api.post("/update-education", payload);		 

			// Update local state list
			const updated = [...educationList];
			updated[editingIndex] = res.data;
			setEducationList(res.data);
			setEditingIndex(null);
		}else {
			const api = axios.create({
				baseURL: import.meta.env.VITE_API_BASE_URL,
				headers: { "Content-Type": "application/json" },
			});
			const res = await api.post("/update-education", payload);

			setEducationList(res.data);
		}

		// --- Reset form ---
		setForm({});
		setGradingType("");

		// --- Handle response feedback ---
		const code = res?.data?.responseCode?.toString();
		if (code === "200") {
		  alert(res?.data?.responseMsg || "✅ Education info saved successfully!");
		} else {
		  alert(res?.data?.responseMsg || "✅ Education info updated successfully!");
		}
	  } catch (err) {
		console.error("Error saving education:", err);
		alert("❌ Failed to save education info. Please try again.");
	  }
	};

  const handleEdit = (index) => {
    setEditingIndex(index);
    setForm(educationList[index]);
    setGradingType(educationList[index].gradingSystem || "");
  };

  const handleCancel = () => {
    setEditingIndex(null);
    setForm({});
    setGradingType("");
  };

  if (loading) {
    return (
      <div className="text-center text-blue-600 py-8">
        Loading Education Information...
      </div>
    );
  }
	const education_level=["A Level","Bachelor/Honors","Diploma","Higher Secondary","JSC","Masters","O Level","PhD (Doctor of Philosophy)","PSC","Secondary","Alim","Dakhil","Fazil","Kamil","Others"];
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl p-6 shadow-md space-y-8"
    >
      <h2 className="text-2xl font-semibold text-[#1A2B63] mb-4">
        Education Information
      </h2>

      {/* ==================== ADD / EDIT FORM ==================== */}
      <form className="grid grid-cols-2 md:grid-cols-3 gap-5">
		<div className="flex flex-col">
		  <label className="text-gray-600 text-sm font-semibold mb-1">
			Education Level
		  </label>
		  <Select
			name="educationLevel"
			className="text-sm"
			options={education_level.map((cat) => ({
			  value: cat,
			  label: cat,
			}))}
			placeholder="Search or select Education Level..."
			value={
			  form.educationLevel
				? {
					value: form.educationLevel,
					label: education_level.find((c) => c === form.educationLevel) || form.educationLevel,
				  }
				: null
			}
			onChange={(selected) =>
			  setForm((prev) => ({
				...prev,
				educationLevel: selected ? selected.value : "",
			  }))
			}
			isClearable
			isSearchable
		  />
		</div>
        {[          
          { label: "Degree", name: "degree" },
          { label: "Group/Major/Concentration", name: "subject" },
          { label: "Appeared/Ongoing", name: "appeared", type: "select", options: ["Appeared", "Ongoing", "Completed"] },
        ].map((field, i) => (
          <div key={i} className="flex flex-col">
            <label className="text-gray-700 text-sm font-semibold mb-1">{field.label}</label>
            {field.type === "select" ? (
              <select
                name={field.name}
                value={form[field.name] || ""}
                onChange={handleChange}
                className="border border-blue-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 outline-none transition-all duration-200"
              >
                <option value="">Select...</option>
                {field.options.map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            ) : (
              <input
                name={field.name}
                value={form[field.name] || ""}
                onChange={handleChange}
                className="border border-blue-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 outline-none transition-all duration-200"
              />
            )}
          </div>
        ))}

        {/* Grading System */}
        <div className="flex flex-col">
          <label className="text-gray-700 text-sm font-semibold mb-1">Grading System</label>
          <select
            name="gradingSystem"
            value={gradingType}
            onChange={(e) => {
              setGradingType(e.target.value);
              handleChange(e);
            }}
            className="border border-blue-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 outline-none transition-all duration-200"
          >
            <option value="">Select Grading System</option>
            <option value="Class">Class</option>
            <option value="Division">Division</option>
            <option value="Grade">Grade (GPA/CGPA)</option>
          </select>
        </div>

        {/* Conditional Grading Fields */}
        {gradingType === "Class" || gradingType === "Division" ? (
          <>
            <div className="flex flex-col">
              <label className="text-gray-700 text-sm font-semibold mb-1">Result</label>
              <input
                name="result"
                value={form.result || ""}
                onChange={handleChange}
                className="border border-blue-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 outline-none transition-all duration-200"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-gray-700 text-sm font-semibold mb-1">Marks %</label>
              <input
                name="marks"
                type="number"
                value={form.marks || ""}
                onChange={handleChange}
                className="border border-blue-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 outline-none transition-all duration-200"
              />
            </div>
          </>
        ) : gradingType === "Grade" ? (
          <>
            <div className="flex flex-col">
              <label className="text-gray-700 text-sm font-semibold mb-1">GPA/CGPA</label>
              <input
                name="cgpa"
                type="number"
                step="0.01"
                value={form.cgpa || ""}
                onChange={handleChange}
                className="border border-blue-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 outline-none transition-all duration-200"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-gray-700 text-sm font-semibold mb-1">CGPA Scale</label>
              <input
                name="cgpaScale"
                type="number"
                step="0.01"
                value={form.cgpaScale || ""}
                onChange={handleChange}
                className="border border-blue-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 outline-none transition-all duration-200"
              />
            </div>
            <div className="flex flex-col">
              <label className="text-gray-700 text-sm font-semibold mb-1">Marks %</label>
              <input
                name="marks"
                type="number"
                value={form.marks || ""}
                onChange={handleChange}
                className="border border-blue-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 outline-none transition-all duration-200"
              />
            </div>
          </>
        ) : null}

        {[
          { label: "Passing Year", name: "passingYear", type: "number" },
          { label: "Institute Name", name: "institute" },
          { label: "Start Date", name: "startDate", type: "date" },
          { label: "End Date", name: "endDate", type: "date" },
        ].map((field, i) => (
          <div key={i} className="flex flex-col">
            <label className="text-gray-700 text-sm font-semibold mb-1">{field.label}</label>
            <input
              type={field.type || "text"}
              name={field.name}
              value={form[field.name] || ""}
              onChange={handleChange}
              className="border border-blue-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 outline-none transition-all duration-200"
            />
          </div>
        ))}

        {/* Achievement */}
        <div className="flex flex-col col-span-full">
          <label className="text-gray-700 text-sm font-semibold mb-1">
            Achievement
          </label>
          <textarea
            name="achievement"
            value={form.achievement || ""}
            onChange={handleChange}
            className="border border-blue-300 rounded-lg p-2 focus:ring-2 focus:ring-blue-500 outline-none transition-all duration-200 min-h-[80px]"
          />
        </div>

        {/* Buttons */}
        <div className="col-span-full flex justify-end gap-3">
          {editingIndex !== null && (
            <Button variant="outline" onClick={handleCancel}>
              Cancel
            </Button>
          )}
          <Button
            onClick={(e) => {
              e.preventDefault();
              handleSave();
            }}
            className="bg-blue-600 text-white"
          >
            {editingIndex !== null ? "Update Education" : "Add Education"}
          </Button>
        </div>
      </form>

      {/* ==================== EDUCATION LIST ==================== */}
      <div className="mt-8 space-y-4">
        <h3 className="text-xl font-semibold text-[#1A2B63]">
          Education History
        </h3>

        {Array.isArray(educationList) && educationList.length > 0 ? (
          educationList.map((edu, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 bg-blue-50 rounded-lg flex justify-between items-start shadow-sm hover:shadow-md transition-all"
            >
              <div>
                <p className="font-semibold text-blue-800">
                  {edu.degree} - {edu.institute}
                </p>
                <p className="text-sm text-gray-600">
                  {edu.educationLevel} | {edu.subject}
                </p>
                <p className="text-sm text-gray-600">
                  {edu.passingYear
                    ? `Passing Year: ${edu.passingYear}`
                    : `${edu.startDate} - ${edu.endDate || "Present"}`}
                </p>
                <p className="text-sm text-gray-600">
                  {edu.gradingSystem === "Grade"
                    ? `CGPA: ${edu.cgpa}/${edu.cgpaScale}`
                    : edu.result
                    ? `${edu.result} (${edu.marks}%)`
                    : null}
                </p>
                {edu.achievement && (
                  <p className="text-sm text-gray-500 italic">
                    Achievement: {edu.achievement}
                  </p>
                )}
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={() => handleEdit(index)}
              >
                Edit
              </Button>
            </motion.div>
          ))
        ) : (
          <p className="text-gray-500 italic text-center">
            No education records added yet.
          </p>
        )}
      </div>
    </motion.div>
  );
};

export default EducationInfo;
